/**
 * VerifyEmailPage - Handles email verification from link click
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const tokenHash = searchParams.get('token_hash') || '';
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!tokenHash) {
      setStatus('error');
      setErrorMsg('Invalid or missing verification link.');
      return;
    }

    const controller = new AbortController();

    const verify = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/confirm-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token_hash: tokenHash }),
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        const result = await res.json();

        if (controller.signal.aborted) return;

        if (!res.ok || !result.success) {
          setStatus('error');
          setErrorMsg(result.error?.message || 'Verification failed. The link may have expired.');
          return;
        }

        setStatus('success');
      } catch {
        // Don't update state if the request was cancelled (e.g. StrictMode remount)
        if (controller.signal.aborted) return;
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    };

    verify();

    return () => controller.abort();
  }, [tokenHash]);

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-warm-100 text-center">
            {status === 'loading' && (
              <>
                <div className="w-12 h-12 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <h1 className="text-xl font-display font-bold text-warm-900 mb-2">
                  Verifying your email...
                </h1>
                <p className="text-warm-600">Please wait a moment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-xl font-display font-bold text-warm-900 mb-2">
                  Email Verified!
                </h1>
                <p className="text-warm-600 mb-6">
                  Your email has been verified successfully. You can now sign in to your account.
                </p>
                <Link
                  to="/login"
                  className="btn-primary inline-block px-8 py-3"
                >
                  Sign In
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h1 className="text-xl font-display font-bold text-warm-900 mb-2">
                  Verification Failed
                </h1>
                <p className="text-warm-600 mb-6">{errorMsg}</p>
                <Link
                  to="/login"
                  className="btn inline-block px-8 py-3"
                >
                  Go to Sign In
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
